<template>
	<nav class="cms-breadcrumbs-block" aria-label="Breadcrumb">
		<ol class="cms-breadcrumbs-block__list">
			<li
				v-for="(crumb, idx) in resolvedCrumbs"
				:key="idx"
				class="cms-breadcrumbs-block__item"
			>
				<a
					v-if="!crumb.current"
					:href="crumb.url || '#'"
					class="cms-breadcrumbs-block__link"
					:style="linkStyle"
				>
					<i v-if="idx === 0 && showHomeResolved && homeIcon" :class="'fa fa-' + homeIcon" class="cms-breadcrumbs-block__home-icon"></i>{{ crumb.label }}</a>
				<span
					v-else
					class="cms-breadcrumbs-block__current"
					:style="currentStyle"
					aria-current="page"
				>{{ crumb.label }}</span>
				<span
					v-if="idx < resolvedCrumbs.length - 1"
					class="cms-breadcrumbs-block__separator"
					:style="separatorStyle"
					aria-hidden="true"
				>{{ separator }}</span>
			</li>
		</ol>
	</nav>
</template>

<script>
/**
 * BreadcrumbsBlock.vue
 * ────────────────────
 * Renders a breadcrumb navigation trail.
 *
 * In the live site this block reads the page hierarchy from the CMS to build
 * the crumbs automatically. In the editor it displays configurable placeholder
 * crumbs so authors can preview the styling.
 *
 * Props:
 * - separator:        Character(s) between crumbs (e.g. '/', '›', '»', '•')
 * - linkColor:        CSS color for ancestor links
 * - currentColor:     CSS color for the active (current) crumb
 * - separatorColor:   CSS color for the separator
 * - showHome:         Whether to prepend a Home crumb
 * - homeLabel:        Label for the Home crumb (default 'Home')
 * - homeIcon:         Font Awesome icon for the Home link (default 'home')
 * - previewCrumbs:    JSON string of custom placeholder crumbs for editor preview
 *
 * Typography, padding, background and visibility are wrapper traits; the
 * breadcrumb text inherits the wrapper's font.
 */

export default {
	name: 'BreadcrumbsBlock',
	inheritAttrs: false,

	props: {
		separator: {
			type: String,
			default: '›'
		},
		linkColor: {
			type: String,
			default: '#FB6A00'
		},
		currentColor: {
			type: String,
			default: '#7f8c99'
		},
		separatorColor: {
			type: String,
			default: '#DDDEE0'
		},
		showHome: {
			type: [Boolean, String],
			default: true
		},
		homeLabel: {
			type: String,
			default: 'Home'
		},
		homeIcon: {
			type: String,
			default: 'home'
		},
		previewCrumbs: {
			type: String,
			default: ''
		}
	},

	computed: {
		showHomeResolved: function () {
			return this.showHome === true || this.showHome === 'true';
		},

		/**
		 * In the editor we show configurable placeholder crumbs.
		 * On the live site the CMS theme layer supplies the real hierarchy
		 * via window.__CMS_PAGE_CRUMBS (set by the server-rendered template).
		 */
		resolvedCrumbs: function () {
			// Live-site crumbs provided by the template layer
			if (typeof window !== 'undefined' && window.__CMS_PAGE_CRUMBS) {
				var liveCrumbs = window.__CMS_PAGE_CRUMBS;
				return this.showHomeResolved
					? liveCrumbs
					: liveCrumbs.filter(function (c) { return c.label !== 'Home'; });
			}

			// Editor / custom preview crumbs
			if (this.previewCrumbs) {
				try {
					var parsed = JSON.parse(this.previewCrumbs);
					if (Array.isArray(parsed) && parsed.length) {
						return this.showHomeResolved
							? parsed
							: parsed.filter(function (c) { return c.label !== 'Home'; });
					}
				} catch (e) { /* fall through to defaults */ }
			}

			// Default placeholder crumbs
			var defaults = [
				{ label: this.homeLabel || 'Home', url: '/', current: false },
				{ label: 'Category', url: '#', current: false },
				{ label: 'Current Page', url: '', current: true }
			];
			return this.showHomeResolved ? defaults : defaults.slice(1);
		},

		linkStyle: function () {
			return {
				color: this.linkColor || '#FB6A00',
				textDecoration: 'none'
			};
		},

		currentStyle: function () {
			return {
				color: this.currentColor || '#7f8c99'
			};
		},

		separatorStyle: function () {
			return {
				color: this.separatorColor || '#DDDEE0'
			};
		}
	}
};
</script>

<style scoped>
.cms-breadcrumbs-block {
	display: block;
	width: 100%;
	font: inherit;
}

.cms-breadcrumbs-block__list {
	display: inline-flex;
	flex-wrap: wrap;
	align-items: center;
	max-width: 100%;
	vertical-align: middle;
	list-style: none;
	margin: 0;
	padding: 0;
	gap: 0.25rem;
}

.cms-breadcrumbs-block__item {
	display: flex;
	align-items: center;
	gap: 0.25rem;
}

.cms-breadcrumbs-block__link {
	transition: opacity 0.15s;
}
.cms-breadcrumbs-block__link:hover {
	opacity: 0.75;
	text-decoration: underline !important;
}

.cms-breadcrumbs-block__home-icon {
	margin-right: 0.3em;
}

.cms-breadcrumbs-block__current {
	cursor: default;
}

.cms-breadcrumbs-block__separator {
	user-select: none;
	padding: 0 0.1rem;
}
</style>
